import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BellRing, Send, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const ELEMENTS = { fiche: "Fiche du dossier de présentation", charte: "Charte RSE", bilan: "Bilan RSE annuel (novembre à mars)" };
const dateFr = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
const erreur = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

export default function RelancesPage() {
  const [data, setData] = useState(null);
  const [p, setP] = useState(null);
  const [choix, setChoix] = useState([]);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/relances/apercu`);
      setData(r.data);
      setP(r.data.parametres);
    } catch (e) { toast.error(erreur(e, "Chargement impossible")); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async () => {
    try {
      await axios.put(`${API}/relances/parametres`, p);
      toast.success(p.actif ? "Relances automatiques activées" : "Réglages enregistrés");
      charger();
    } catch (e) { toast.error(erreur(e, "Enregistrement impossible")); }
  };

  const envoyer = async () => {
    if (!choix.length) return;
    if (!window.confirm(`Envoyer maintenant une relance à ${choix.length} food truck(s) ?`)) return;
    setEnvoi(true);
    try {
      const r = await axios.post(`${API}/relances/envoyer`, { food_truck_ids: choix, force: true });
      toast.success(`${r.data.envoyes} relance(s) envoyée(s)${r.data.erreurs ? `, ${r.data.erreurs} en erreur` : ""}`);
      setChoix([]);
      charger();
    } catch (e) { toast.error(erreur(e, "Envoi impossible")); }
    setEnvoi(false);
  };

  if (!data || !p) return null;
  const aRelancer = data.lignes.filter((l) => l.manques.length);
  const basculer = (id) => setChoix((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <div className="space-y-4" data-testid="relances-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
          <BellRing className="w-6 h-6 text-primary" /> Relances adhérents
        </h1>
        <p className="text-sm text-muted-foreground">
          Les food trucks actifs dont l'espace est incomplet reçoivent un e-mail et une notification, avec un lien direct
          vers ce qu'il reste à faire.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Réglages</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" checked={p.actif} onChange={(e) => setP({ ...p, actif: e.target.checked })} />
            Relances automatiques activées
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <span>Au plus une relance tous les</span>
            <select className="h-9 border rounded-md px-2" value={p.frequence_jours}
              onChange={(e) => setP({ ...p, frequence_jours: parseInt(e.target.value, 10) })}>
              {[7, 14, 30].map((j) => <option key={j} value={j}>{j} jours</option>)}
            </select>
            <span>par food truck</span>
          </div>
          <div className="space-y-1">
            <p className="text-muted-foreground">Éléments relancés :</p>
            {Object.entries(ELEMENTS).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={!!p.elements[k]}
                  onChange={(e) => setP({ ...p, elements: { ...p.elements, [k]: e.target.checked } })} />
                {v}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.annee_bilan
              ? `Campagne de bilan RSE en cours : bilan ${data.annee_bilan}.`
              : "Hors campagne de bilan RSE (avril à octobre) : le bilan n'est pas relancé."}
            {" "}Les envois automatiques partent avec les tâches quotidiennes de l'application.
          </p>
          <Button onClick={enregistrer}><Save className="w-4 h-4 mr-1" /> Enregistrer les réglages</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base">{aRelancer.length} food truck(s) à compléter sur {data.lignes.length} actifs</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setChoix(aRelancer.filter((l) => l.emails.length).map((l) => l.food_truck_id))}>
              Tout sélectionner
            </Button>
            <Button size="sm" disabled={!choix.length || envoi} onClick={envoyer}>
              <Send className="w-4 h-4 mr-1" /> Relancer maintenant ({choix.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {aRelancer.length === 0 && <p className="p-6 text-center text-muted-foreground">Tous les espaces sont complets.</p>}
          {aRelancer.map((l) => (
            <label key={l.food_truck_id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/30">
              <input type="checkbox" className="mt-1" disabled={!l.emails.length}
                checked={choix.includes(l.food_truck_id)} onChange={() => basculer(l.food_truck_id)} />
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">{l.nom}</p>
                  {l.du && p.actif && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">Relance au prochain passage</Badge>}
                  {!l.emails.length && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Aucun e-mail</Badge>}
                </div>
                <ul className="text-sm text-muted-foreground list-disc pl-5">
                  {l.manques.map((m) => <li key={m.code}>{m.texte}</li>)}
                </ul>
                <p className="text-xs text-muted-foreground">
                  {l.emails.join(", ")}
                  {l.derniere_relance ? ` · dernière relance le ${dateFr(l.derniere_relance)}` : " · jamais relancé"}
                </p>
              </div>
            </label>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
